import React, { useState, useEffect } from 'react';
import * as mui from '@mui/material';
import auditService from '../../../../services/AuditService';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import WarningIcon from '@mui/icons-material/Warning';
import GppBadIcon from '@mui/icons-material/GppBad';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SecurityIcon from '@mui/icons-material/Security';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewModuleIcon from '@mui/icons-material/ViewModule';

const RiskIdentifier = ({ 
  app, 
  company, 
  appId, 
  companyId, 
  nextStep,
  selectRisk,
  refreshData
}) => {
  // State for risks
  const [availableRisks, setAvailableRisks] = useState([]);
  const [mappedRisks, setMappedRisks] = useState([]);
  const [filteredRisks, setFilteredRisks] = useState([]);
  const [filteredMappedRisks, setFilteredMappedRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [mappedSearchTerm, setMappedSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [riskTypeFilter, setRiskTypeFilter] = useState('all');
  const [mappedRiskTypeFilter, setMappedRiskTypeFilter] = useState('all');
  const [tabValue, setTabValue] = useState('1');
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'group', or 'list'
  
  // State for UI
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [processingRiskId, setProcessingRiskId] = useState(null);

  useEffect(() => {
    // Restore UI state from localStorage
    const savedTab = localStorage.getItem(`risk-identifier-tab-${appId}-${companyId}`);
    if (savedTab) {
      setTabValue(savedTab);
    }
    
    const savedViewMode = localStorage.getItem(`risk-identifier-viewmode-${appId}-${companyId}`);
    if (savedViewMode && ['grid', 'list', 'group'].includes(savedViewMode)) {
      setViewMode(savedViewMode);
    }
    
    // Restore search terms and filters
    const savedSearchTerm = localStorage.getItem(`risk-identifier-search-${appId}-${companyId}`);
    if (savedSearchTerm) {
      setSearchTerm(savedSearchTerm);
    }
    
    const savedMappedSearchTerm = localStorage.getItem(`risk-identifier-mapped-search-${appId}-${companyId}`);
    if (savedMappedSearchTerm) {
      setMappedSearchTerm(savedMappedSearchTerm);
    }
    
    const savedRiskTypeFilter = localStorage.getItem(`risk-identifier-type-filter-${appId}-${companyId}`);
    if (savedRiskTypeFilter) {
      setRiskTypeFilter(savedRiskTypeFilter);
    }
    
    const savedMappedRiskTypeFilter = localStorage.getItem(`risk-identifier-mapped-type-filter-${appId}-${companyId}`);
    if (savedMappedRiskTypeFilter) {
      setMappedRiskTypeFilter(savedMappedRiskTypeFilter);
    }
    
    const loadRisks = async () => {
      setLoading(true);
      try {
        // Get all risks from the library
        const allRisks = await auditService.fetchRisk();
        
        // Get risks already mapped to this app
        const mappingResponse = await auditService.fetchRiskMappingbyApp(appId, companyId);
        const mappedRiskData = mappingResponse?.risklist_data || [];
        
        setMappedRisks(mappedRiskData);
        setFilteredMappedRisks(mappedRiskData);
        
        // Filter out risks already mapped to this app
        const mappedRiskIds = mappedRiskData.map(risk => risk.id);
        
        // For new projects, ensure all risks are shown in the available risks
        if (allRisks && allRisks.length > 0) {
          const availableRisksList = allRisks.filter(risk => !mappedRiskIds.includes(risk.id));
          setAvailableRisks(availableRisksList);
          setFilteredRisks(availableRisksList);
        } else {
          console.error('No risks found in the risk library');
          setAvailableRisks([]);
          setFilteredRisks([]);
        }
      } catch (error) {
        console.error('Error loading risks:', error);
        // Even if there's an error with mapped risks, still try to show available risks
        try {
          const allRisks = await auditService.fetchRisk();
          if (allRisks && allRisks.length > 0) {
            setAvailableRisks(allRisks);
            setFilteredRisks(allRisks);
          }
        } catch (innerError) {
          console.error('Error fetching risk library:', innerError);
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadRisks();
  }, [appId, companyId]);

  // Filter available risks based on search term and filters
  useEffect(() => {
    let filtered = [...availableRisks];
    
    // Apply search term
    if (searchTerm) {
      filtered = filtered.filter(
        risk => 
          risk.RISK_ID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          risk.RISK_NAME?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          risk.RISK_DESCRIPTION?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply risk type filter
    if (riskTypeFilter !== 'all') {
      filtered = filtered.filter(risk => risk.RISK_TYPE === riskTypeFilter);
    }
    
    setFilteredRisks(filtered);
  }, [searchTerm, riskTypeFilter, availableRisks]);

  // Filter mapped risks based on search term and filters
  useEffect(() => {
    let filtered = [...mappedRisks];
    
    // Apply search term
    if (mappedSearchTerm) {
      filtered = filtered.filter(
        risk => 
          risk.RISK_ID?.toLowerCase().includes(mappedSearchTerm.toLowerCase()) ||
          risk.RISK_NAME?.toLowerCase().includes(mappedSearchTerm.toLowerCase()) ||
          risk.RISK_DESCRIPTION?.toLowerCase().includes(mappedSearchTerm.toLowerCase())
      );
    }
    
    // Apply risk type filter
    if (mappedRiskTypeFilter !== 'all') {
      filtered = filtered.filter(risk => risk.RISK_TYPE === mappedRiskTypeFilter);
    }
    
    setFilteredMappedRisks(filtered);
  }, [mappedSearchTerm, mappedRiskTypeFilter, mappedRisks]);

  // Handle marking a risk as relevant
  const handleMarkAsRelevant = async (risk) => {
    setProcessingRiskId(risk.id);
    
    try {
      // Get the current project ID from URL or app state
      // Check if there is a query parameter for project ID or use the first project if none specified
      const urlParams = new URLSearchParams(window.location.search);
      let projectId = urlParams.get('project') || localStorage.getItem('currentProjectId');
      
      // If no project ID is found in URL or localStorage, we need to fetch available projects
      if (!projectId) {
        try {
          // Try to get the first available project for this company
          const projects = await auditService.fetchProjects();
          if (projects && projects.length > 0) {
            // Filter projects for this company
            const companyProjects = projects.filter(p => p.COMPANY_ID === companyId);
            if (companyProjects.length > 0) {
              projectId = companyProjects[0].id; // Use the first project's ID
              localStorage.setItem('currentProjectId', projectId); // Save for future use
            }
          }
        } catch (error) {
          console.error('Error fetching projects:', error);
        }
      }
      
      const mappingData = {
        RISK_ID: risk.id,
        APP_NAME: appId,
        COMPANY_ID: companyId,
        AUDIT_LIST: projectId // Use the actual project ID if found
      };
      
      await auditService.createRiskMapping(mappingData);
      
      // Update lists
      setAvailableRisks(prev => prev.filter(r => r.id !== risk.id));
      
      // Get the updated risk with the mapping data
      const updatedRiskMapping = await auditService.fetchRiskMappingbyApp(appId, companyId);
      if (updatedRiskMapping && updatedRiskMapping.risklist_data) {
        setMappedRisks(updatedRiskMapping.risklist_data);
        setFilteredMappedRisks(updatedRiskMapping.risklist_data);
      }
      
      // Show success message
      setSuccessMessage(`Risk "${risk.RISK_ID}" marked as relevant`);
      setShowSuccessAlert(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccessAlert(false);
      }, 3000);
      
      // Refresh parent data
      if (refreshData) refreshData();
      
    } catch (error) {
      console.error('Error marking risk as relevant:', error);
    } finally {
      setProcessingRiskId(null);
    }
  };

  // Handle quick risk rating
  const handleQuickRating = async (risk, rating) => {
    // If rating is already set or we're processing, don't do anything
    if (risk.RATING === rating || processingRiskId === risk.id) return;
    
    console.log("handleQuickRating called with:", risk.id, rating);
    setProcessingRiskId(risk.id);
    
    // Update local state immediately for better UX
    setMappedRisks(prev => 
      prev.map(r => r.id === risk.id ? {...r, RATING: rating} : r)
    );
    setFilteredMappedRisks(prev => 
      prev.map(r => r.id === risk.id ? {...r, RATING: rating} : r)
    );
    
    try {
      // Get the project ID consistently with handleMarkAsRelevant
      const urlParams = new URLSearchParams(window.location.search);
      let projectId = urlParams.get('project') || localStorage.getItem('currentProjectId');
      
      // If no project ID is found in URL or localStorage, we need to fetch available projects
      if (!projectId) {
        try {
          // Try to get the first available project for this company
          const projects = await auditService.fetchProjects();
          if (projects && projects.length > 0) {
            // Filter projects for this company
            const companyProjects = projects.filter(p => p.COMPANY_ID === companyId);
            if (companyProjects.length > 0) {
              projectId = companyProjects[0].id; // Use the first project's ID
              localStorage.setItem('currentProjectId', projectId); // Save for future use
            }
          }
        } catch (error) {
          console.error('Error fetching projects:', error);
        }
      }
      
      const ratingData = {
        RISK_ID: risk.id,
        APP_NAME: appId,
        COMPANY_ID: companyId,
        RATING: rating,
        AUDIT_LIST: projectId
      };
      
      // Make a single API call to update the rating
      await auditService.updateRiskMappingbyMapID(appId, risk.id, companyId, ratingData);
      
      // Show success message
      setSuccessMessage(`Risk "${risk.RISK_ID}" rated as ${rating}`);
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 3000);
      
      // Call refreshData with a slight delay to avoid UI jank
      if (refreshData) setTimeout(() => refreshData(), 300);
    } catch (error) {
      console.error('Error rating risk:', error);
      // Revert the local state if the API call fails
      setMappedRisks(prev => 
        prev.map(r => r.id === risk.id ? {...r, RATING: risk.RATING} : r)
      );
      setFilteredMappedRisks(prev => 
        prev.map(r => r.id === risk.id ? {...r, RATING: risk.RATING} : r)
      );
    } finally {
      // Release the processing state
      setProcessingRiskId(null);
    }
  };

  // Handle removing a risk
  const handleRemoveRisk = async (risk) => {
    setProcessingRiskId(risk.id);
    
    try {
      await auditService.deleteRiskMappingbyMapID(appId, risk.id, companyId);
      
      // Update mapped risks list
      const updatedMappedRisks = mappedRisks.filter(r => r.id !== risk.id);
      setMappedRisks(updatedMappedRisks);
      setFilteredMappedRisks(updatedMappedRisks);
      
      // Add back to available risks
      const riskData = await auditService.fetchRiskById(risk.id);
      if (riskData) {
        setAvailableRisks(prev => [...prev, riskData]);
      }
      
      // Show success message
      setSuccessMessage(`Risk "${risk.RISK_ID}" removed`);
      setShowSuccessAlert(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccessAlert(false);
      }, 3000);
      
      // Refresh parent data
      if (refreshData) refreshData();
      
    } catch (error) {
      console.error('Error removing risk:', error);
    } finally {
      setProcessingRiskId(null);
    }
  };

  // Get risk rating badge color
  const getRatingColor = (rating) => {
    switch(rating) {
      case 'High': return '#d32f2f';
      case 'Medium': return '#ff9800';
      case 'Low': return '#2e7d32';
      default: return '#757575';
    }
  };

  // Get risk icon based on type
  const getRiskTypeIcon = (type) => {
    switch(type) {
      case 'IT': return <SecurityIcon fontSize="small" />;
      case 'Business Process': return <ErrorOutlineIcon fontSize="small" />;
      case 'Operational': return <WarningIcon fontSize="small" />;
      case 'Entity Level': return <GppBadIcon fontSize="small" />;
      default: return <ErrorOutlineIcon fontSize="small" />;
    }
  };

  // Handle selecting a risk for assessment or mapping
  const handleSelectRisk = (risk) => {
    if (selectRisk) {
      selectRisk(risk);
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Save tab to localStorage
    localStorage.setItem(`risk-identifier-tab-${appId}-${companyId}`, newValue);
  };

  // Get risk type options for filter
  const riskTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'IT', label: 'IT' },
    { value: 'Business Process', label: 'Business Process' },
    { value: 'Operational', label: 'Operational' },
    { value: 'Entity Level', label: 'Entity Level' }
  ];

  // Group risks by type
  const groupRisksByType = (risks) => {
    const grouped = {};
    
    riskTypeOptions.forEach(option => {
      if (option.value !== 'all') {
        grouped[option.value] = risks.filter(risk => risk.RISK_TYPE === option.value);
      }
    });
    
    return grouped;
  };

  // Risk card component
  const RiskCard = ({ risk, isRelevant, onAction }) => (
    <mui.Card 
      elevation={1}
      sx={{ 
        mb: 2,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3
        }
      }}
    >
      <mui.CardContent sx={{ pb: 1 }}>
        <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
            <mui.Avatar 
              sx={{ 
                bgcolor: 'primary.light', 
                width: 32, 
                height: 32,
                mr: 1
              }}
            >
              {getRiskTypeIcon(risk.RISK_TYPE)}
            </mui.Avatar>
            <mui.Typography variant="subtitle1" fontWeight="bold">
              {risk.RISK_ID}
            </mui.Typography>
          </mui.Box>
          
          <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
            {risk.RATING && (
              <mui.Chip 
                size="small"
                label={risk.RATING}
                sx={{ 
                  backgroundColor: `${getRatingColor(risk.RATING)}15`,
                  color: getRatingColor(risk.RATING),
                  mr: 1,
                  fontWeight: 'bold'
                }}
              />
            )}
            <mui.Chip 
              size="small" 
              label={risk.RISK_TYPE} 
              variant="outlined"
            />
          </mui.Box>
        </mui.Box>
        
        <mui.Typography variant="body1" gutterBottom>
          {risk.RISK_NAME}
        </mui.Typography>
        
        <mui.Typography 
          variant="body2" 
          color="textSecondary"
          sx={{
            display: '-webkit-box',
            overflow: 'hidden',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            mb: 2
          }}
        >
          {risk.RISK_DESCRIPTION}
        </mui.Typography>
      </mui.CardContent>
      
      <mui.CardActions sx={{ justifyContent: 'space-between', px: 2, pt: 0, pb: 2 }}>
        {isRelevant ? (
          <>
            <mui.Button 
              variant="outlined" 
              color="error" 
              size="small"
              startIcon={processingRiskId === risk.id ? <mui.CircularProgress size={18} /> : null}
              onClick={() => onAction(risk)}
              disabled={processingRiskId === risk.id}
            >
              {processingRiskId !== risk.id && "Remove"}
            </mui.Button>
            
            <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Quick Assessment Chips */}
              {isRelevant && (
                <mui.Box sx={{ display: 'flex', gap: 0.5, mr: 1 }}>
                  <mui.Button
                    variant={risk.RATING === 'Low' ? "contained" : "outlined"}
                    size="small"
                    onClick={() => handleQuickRating(risk, 'Low')}
                    disabled={processingRiskId === risk.id}
                    sx={{
                      bgcolor: risk.RATING === 'Low' ? '#2e7d32' : 'transparent',
                      color: risk.RATING === 'Low' ? 'white' : '#2e7d32',
                      borderColor: '#2e7d32',
                      '&:hover': {
                        bgcolor: risk.RATING === 'Low' ? '#2e7d32' : '#2e7d3210',
                      },
                      minWidth: '32px',
                      height: '24px',
                      px: 1
                    }}
                  >
                    L
                  </mui.Button>
                  <mui.Button
                    variant={risk.RATING === 'Medium' ? "contained" : "outlined"}
                    size="small"
                    onClick={() => handleQuickRating(risk, 'Medium')}
                    disabled={processingRiskId === risk.id}
                    sx={{
                      bgcolor: risk.RATING === 'Medium' ? '#ff9800' : 'transparent',
                      color: risk.RATING === 'Medium' ? 'white' : '#ff9800',
                      borderColor: '#ff9800',
                      '&:hover': {
                        bgcolor: risk.RATING === 'Medium' ? '#ff9800' : '#ff980010',
                      },
                      minWidth: '32px',
                      height: '24px',
                      px: 1
                    }}
                  >
                    M
                  </mui.Button>
                  <mui.Button
                    variant={risk.RATING === 'High' ? "contained" : "outlined"}
                    size="small"
                    onClick={() => handleQuickRating(risk, 'High')}
                    disabled={processingRiskId === risk.id}
                    sx={{
                      bgcolor: risk.RATING === 'High' ? '#d32f2f' : 'transparent',
                      color: risk.RATING === 'High' ? 'white' : '#d32f2f',
                      borderColor: '#d32f2f',
                      '&:hover': {
                        bgcolor: risk.RATING === 'High' ? '#d32f2f' : '#d32f2f10',
                      },
                      minWidth: '32px',
                      height: '24px',
                      px: 1
                    }}
                  >
                    H
                  </mui.Button>
                  {processingRiskId === risk.id && (
                    <mui.CircularProgress size={16} />
                  )}
                </mui.Box>
              )}
              
              <mui.Button
                color="primary"
                size="small"
                onClick={() => handleSelectRisk(risk)}
              >
                {!risk.RATING || risk.RATING === 'Not Set' 
                  ? "Rate Risk" 
                  : !risk.CONTROL_DETAILS || risk.CONTROL_DETAILS.length === 0
                    ? "Map Controls"
                    : "View Details"}
              </mui.Button>
            </mui.Box>
          </>
        ) : (
          <mui.Button 
            variant="contained" 
            color="primary" 
            size="small"
            startIcon={processingRiskId === risk.id ? <mui.CircularProgress size={18} color="inherit" /> : <AddCircleOutlineIcon />}
            onClick={() => onAction(risk)}
            disabled={processingRiskId === risk.id}
            fullWidth
          >
            {processingRiskId !== risk.id && "Mark as Relevant"}
          </mui.Button>
        )}
      </mui.CardActions>
    </mui.Card>
  );

  // Risk list item component
  const RiskListItem = ({ risk, isRelevant, onAction }) => (
    <mui.Paper 
      elevation={1}
      sx={{ 
        mb: 2,
        p: 2,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3
        }
      }}
    >
      <mui.Grid container spacing={2} alignItems="center">
        {/* Risk ID and Type */}
        <mui.Grid item xs={12} sm={4}>
          <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
            <mui.Avatar 
              sx={{ 
                bgcolor: 'primary.light', 
                width: 32, 
                height: 32,
                mr: 1
              }}
            >
              {getRiskTypeIcon(risk.RISK_TYPE)}
            </mui.Avatar>
            <mui.Box>
              <mui.Typography variant="subtitle1" fontWeight="bold">
                {risk.RISK_ID}
              </mui.Typography>
              <mui.Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <mui.Chip 
                  size="small" 
                  label={risk.RISK_TYPE} 
                  variant="outlined"
                />
                {risk.RATING && (
                  <mui.Chip 
                    size="small"
                    label={risk.RATING}
                    sx={{ 
                      backgroundColor: `${getRatingColor(risk.RATING)}15`,
                      color: getRatingColor(risk.RATING),
                      ml: 1,
                      fontWeight: 'bold'
                    }}
                  />
                )}
              </mui.Box>
            </mui.Box>
          </mui.Box>
        </mui.Grid>
        
        {/* Risk Name and Description */}
        <mui.Grid item xs={12} sm={5}>
          <mui.Typography variant="body1" fontWeight="medium">
            {risk.RISK_NAME}
          </mui.Typography>
          <mui.Typography 
            variant="body2" 
            color="textSecondary"
            sx={{
              display: '-webkit-box',
              overflow: 'hidden',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2
            }}
          >
            {risk.RISK_DESCRIPTION}
          </mui.Typography>
        </mui.Grid>
        
        {/* Actions */}
        <mui.Grid item xs={12} sm={3} sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, gap: 1 }}>
          {isRelevant ? (
            <>
              {/* Quick Assessment Chips */}
              <mui.Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mr: 1 }}>
                <mui.Button
                  variant={risk.RATING === 'Low' ? "contained" : "outlined"}
                  size="small"
                  onClick={() => handleQuickRating(risk, 'Low')}
                  disabled={processingRiskId === risk.id}
                  sx={{
                    bgcolor: risk.RATING === 'Low' ? '#2e7d32' : 'transparent',
                    color: risk.RATING === 'Low' ? 'white' : '#2e7d32',
                    borderColor: '#2e7d32',
                    '&:hover': {
                      bgcolor: risk.RATING === 'Low' ? '#2e7d32' : '#2e7d3210',
                    },
                    minWidth: '32px',
                    height: '24px',
                    px: 1
                  }}
                >
                  L
                </mui.Button>
                <mui.Button
                  variant={risk.RATING === 'Medium' ? "contained" : "outlined"}
                  size="small"
                  onClick={() => handleQuickRating(risk, 'Medium')}
                  disabled={processingRiskId === risk.id}
                  sx={{
                    bgcolor: risk.RATING === 'Medium' ? '#ff9800' : 'transparent',
                    color: risk.RATING === 'Medium' ? 'white' : '#ff9800',
                    borderColor: '#ff9800',
                    '&:hover': {
                      bgcolor: risk.RATING === 'Medium' ? '#ff9800' : '#ff980010',
                    },
                    minWidth: '32px',
                    height: '24px',
                    px: 1
                  }}
                >
                  M
                </mui.Button>
                <mui.Button
                  variant={risk.RATING === 'High' ? "contained" : "outlined"}
                  size="small"
                  onClick={() => handleQuickRating(risk, 'High')}
                  disabled={processingRiskId === risk.id}
                  sx={{
                    bgcolor: risk.RATING === 'High' ? '#d32f2f' : 'transparent',
                    color: risk.RATING === 'High' ? 'white' : '#d32f2f',
                    borderColor: '#d32f2f',
                    '&:hover': {
                      bgcolor: risk.RATING === 'High' ? '#d32f2f' : '#d32f2f10',
                    },
                    minWidth: '32px',
                    height: '24px',
                    px: 1
                  }}
                >
                  H
                </mui.Button>
                {processingRiskId === risk.id && (
                  <mui.CircularProgress size={16} />
                )}
              </mui.Box>
              
              <mui.Box sx={{ display: 'flex', gap: 1 }}>
                <mui.Button 
                  variant="outlined" 
                  color="error" 
                  size="small"
                  startIcon={processingRiskId === risk.id ? <mui.CircularProgress size={18} /> : null}
                  onClick={() => onAction(risk)}
                  disabled={processingRiskId === risk.id}
                >
                  {processingRiskId !== risk.id && "Remove"}
                </mui.Button>
                <mui.Button
                  color="primary"
                  size="small"
                  onClick={() => handleSelectRisk(risk)}
                >
                  {!risk.RATING || risk.RATING === 'Not Set' 
                    ? "Rate Risk" 
                    : !risk.CONTROL_DETAILS || risk.CONTROL_DETAILS.length === 0
                      ? "Map Controls"
                      : "View Details"}
                </mui.Button>
              </mui.Box>
            </>
          ) : (
            <mui.Button 
              variant="contained" 
              color="primary" 
              size="small"
              startIcon={processingRiskId === risk.id ? <mui.CircularProgress size={18} color="inherit" /> : <AddCircleOutlineIcon />}
              onClick={() => onAction(risk)}
              disabled={processingRiskId === risk.id}
            >
              {processingRiskId !== risk.id && "Mark as Relevant"}
            </mui.Button>
          )}
        </mui.Grid>
      </mui.Grid>
    </mui.Paper>
  );

  return (
    <mui.Box>
      {/* Success notification */}
      <mui.Snackbar
        open={showSuccessAlert}
        autoHideDuration={3000}
        onClose={() => setShowSuccessAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <mui.Alert
          severity="success"
          variant="filled"
          icon={<CheckCircleIcon fontSize="inherit" />}
        >
          {successMessage}
        </mui.Alert>
      </mui.Snackbar>
      
      <mui.Typography variant="h5" gutterBottom>
        Risk Identification for {app?.APP_NAME}
      </mui.Typography>
      
      <mui.Typography variant="body1" color="textSecondary" paragraph>
        Review the risks from the risk library and mark the ones that are relevant to this application.
        Relevant risks will need to be rated and mapped to controls for mitigation.
      </mui.Typography>
      
      <TabContext value={tabValue}>
        <mui.Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList onChange={handleTabChange} aria-label="risk tabs">
            <mui.Tab 
              label={
                <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AddCircleOutlineIcon sx={{ mr: 1 }} />
                  <span>Available Risks</span>
                  <mui.Chip 
                    label={filteredRisks.length} 
                    size="small" 
                    sx={{ ml: 1 }} 
                  />
                </mui.Box>
              } 
              value="1" 
            />
            <mui.Tab 
              label={
                <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ mr: 1 }} />
                  <span>Mapped Risks</span>
                  <mui.Chip 
                    label={mappedRisks.length} 
                    size="small" 
                    sx={{ ml: 1 }} 
                    color="primary"
                  />
                </mui.Box>
              } 
              value="2" 
            />
          </TabList>
        </mui.Box>
        
        <TabPanel value="1" sx={{ px: 0 }}>
          {/* Search and filter controls */}
          <mui.Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <mui.TextField
              placeholder="Search risks..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                localStorage.setItem(`risk-identifier-search-${appId}-${companyId}`, value);
              }}
              InputProps={{
                startAdornment: (
                  <mui.InputAdornment position="start">
                    <SearchIcon color="action" />
                  </mui.InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <mui.InputAdornment position="end">
                    <mui.IconButton size="small" onClick={() => setSearchTerm('')}>
                      <mui.Icon fontSize="small">clear</mui.Icon>
                    </mui.IconButton>
                  </mui.InputAdornment>
                )
              }}
            />
            
            <mui.FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
              <mui.InputLabel id="risk-type-filter-label">Risk Type</mui.InputLabel>
              <mui.Select
                labelId="risk-type-filter-label"
                value={riskTypeFilter}
                onChange={(e) => {
                  const value = e.target.value;
                  setRiskTypeFilter(value);
                  localStorage.setItem(`risk-identifier-type-filter-${appId}-${companyId}`, value);
                }}
                label="Risk Type"
                startAdornment={
                  <mui.InputAdornment position="start">
                    <FilterListIcon color="action" sx={{ mr: 1 }} />
                  </mui.InputAdornment>
                }
              >
                {riskTypeOptions.map(option => (
                  <mui.MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </mui.MenuItem>
                ))}
              </mui.Select>
            </mui.FormControl>

            <mui.ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => {
                if (newMode) {
                  setViewMode(newMode);
                  // Save view mode to localStorage
                  localStorage.setItem(`risk-identifier-viewmode-${appId}-${companyId}`, newMode);
                }
              }}
              aria-label="view mode"
              size="small"
            >
              <mui.ToggleButton value="grid" aria-label="grid view">
                <GridViewIcon />
              </mui.ToggleButton>
              <mui.ToggleButton value="list" aria-label="list view">
                <ViewListIcon />
              </mui.ToggleButton>
              <mui.ToggleButton value="group" aria-label="grouped by type">
                <ViewModuleIcon />
              </mui.ToggleButton>
            </mui.ToggleButtonGroup>
          </mui.Box>
          
          {loading ? (
            <mui.Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <mui.CircularProgress />
            </mui.Box>
          ) : filteredRisks.length === 0 ? (
            <mui.Paper 
              sx={{ 
                p: 4, 
                textAlign: 'center',
                backgroundColor: '#f9f9f9',
                borderRadius: 2
              }}
            >
              <mui.Typography variant="h6" color="textSecondary" gutterBottom>
                No risks available
              </mui.Typography>
              <mui.Typography variant="body2" color="textSecondary">
                {searchTerm || riskTypeFilter !== 'all' 
                  ? 'No risks match your search criteria. Try adjusting your filters.' 
                  : 'No risks were found in the risk library. Please ensure the risk library is populated.'}
              </mui.Typography>
              {(!searchTerm && riskTypeFilter === 'all') && (
                <mui.Button 
                  variant="outlined" 
                  color="primary" 
                  sx={{ mt: 2 }}
                  onClick={() => {
                    setLoading(true);
                    auditService.fetchRisk()
                      .then(risks => {
                        if (risks && risks.length > 0) {
                          setAvailableRisks(risks);
                          setFilteredRisks(risks);
                        }
                      })
                      .catch(err => console.error("Error refreshing risks:", err))
                      .finally(() => setLoading(false));
                  }}
                >
                  Refresh Risk Library
                </mui.Button>
              )}
            </mui.Paper>
          ) : viewMode === 'grid' ? (
            <mui.Grid container spacing={2}>
              {filteredRisks.map(risk => (
                <mui.Grid item xs={12} sm={6} md={4} key={risk.id}>
                  <RiskCard 
                    risk={risk} 
                    isRelevant={false} 
                    onAction={handleMarkAsRelevant}
                  />
                </mui.Grid>
              ))}
            </mui.Grid>
          ) : viewMode === 'list' ? (
            <mui.Box>
              {filteredRisks.map(risk => (
                <RiskListItem
                  key={risk.id}
                  risk={risk}
                  isRelevant={false}
                  onAction={handleMarkAsRelevant}
                />
              ))}
            </mui.Box>
          ) : (
            <mui.Box>
              {Object.entries(groupRisksByType(filteredRisks)).map(([type, risks]) => 
                risks.length > 0 && (
                  <mui.Box key={type} sx={{ mb: 4 }}>
                    <mui.Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        mb: 2,
                        pb: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <mui.Avatar 
                        sx={{ 
                          bgcolor: 'primary.main', 
                          width: 32, 
                          height: 32,
                          mr: 1
                        }}
                      >
                        {getRiskTypeIcon(type)}
                      </mui.Avatar>
                      <mui.Typography variant="h6" color="primary.main">
                        {type} Risks
                      </mui.Typography>
                      <mui.Chip 
                        label={risks.length} 
                        size="small" 
                        sx={{ ml: 2 }} 
                        color="primary"
                        variant="outlined"
                      />
                    </mui.Box>
                    <mui.Grid container spacing={2}>
                      {risks.map(risk => (
                        <mui.Grid item xs={12} sm={6} md={4} key={risk.id}>
                          <RiskCard 
                            risk={risk} 
                            isRelevant={false} 
                            onAction={handleMarkAsRelevant}
                          />
                        </mui.Grid>
                      ))}
                    </mui.Grid>
                  </mui.Box>
                )
              )}
            </mui.Box>
          )}
        </TabPanel>
        
        <TabPanel value="2" sx={{ px: 0 }}>
          {/* Search for mapped risks */}
          <mui.Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <mui.TextField
              placeholder="Search mapped risks..."
              variant="outlined"
              size="small"
              fullWidth
              value={mappedSearchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setMappedSearchTerm(value);
                localStorage.setItem(`risk-identifier-mapped-search-${appId}-${companyId}`, value);
              }}
              InputProps={{
                startAdornment: (
                  <mui.InputAdornment position="start">
                    <SearchIcon color="action" />
                  </mui.InputAdornment>
                ),
                endAdornment: mappedSearchTerm && (
                  <mui.InputAdornment position="end">
                    <mui.IconButton size="small" onClick={() => setMappedSearchTerm('')}>
                      <mui.Icon fontSize="small">clear</mui.Icon>
                    </mui.IconButton>
                  </mui.InputAdornment>
                )
              }}
            />
            
            <mui.FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
              <mui.InputLabel id="mapped-risk-type-filter-label">Risk Type</mui.InputLabel>
              <mui.Select
                labelId="mapped-risk-type-filter-label"
                value={mappedRiskTypeFilter}
                onChange={(e) => {
                  const value = e.target.value;
                  setMappedRiskTypeFilter(value);
                  localStorage.setItem(`risk-identifier-mapped-type-filter-${appId}-${companyId}`, value);
                }}
                label="Risk Type"
                startAdornment={
                  <mui.InputAdornment position="start">
                    <FilterListIcon color="action" sx={{ mr: 1 }} />
                  </mui.InputAdornment>
                }
              >
                {riskTypeOptions.map(option => (
                  <mui.MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </mui.MenuItem>
                ))}
              </mui.Select>
            </mui.FormControl>

            <mui.ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => {
                if (newMode) {
                  setViewMode(newMode);
                  // Save view mode to localStorage
                  localStorage.setItem(`risk-identifier-viewmode-${appId}-${companyId}`, newMode);
                }
              }}
              aria-label="view mode"
              size="small"
            >
              <mui.ToggleButton value="grid" aria-label="grid view">
                <GridViewIcon />
              </mui.ToggleButton>
              <mui.ToggleButton value="list" aria-label="list view">
                <ViewListIcon />
              </mui.ToggleButton>
              <mui.ToggleButton value="group" aria-label="grouped by type">
                <ViewModuleIcon />
              </mui.ToggleButton>
            </mui.ToggleButtonGroup>
          </mui.Box>
          
          {loading ? (
            <mui.Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <mui.CircularProgress />
            </mui.Box>
          ) : mappedRisks.length === 0 ? (
            <mui.Paper 
              sx={{ 
                p: 4, 
                textAlign: 'center',
                backgroundColor: '#f9f9f9',
                borderRadius: 2
              }}
            >
              <mui.Typography variant="h6" color="textSecondary" gutterBottom>
                No risks mapped yet
              </mui.Typography>
              <mui.Typography variant="body2" color="textSecondary">
                You haven't marked any risks as relevant for this application yet.
                Switch to the "Available Risks" tab to start identifying relevant risks.
              </mui.Typography>
              <mui.Button 
                variant="outlined" 
                color="primary" 
                sx={{ mt: 2 }}
                onClick={() => setTabValue('1')}
              >
                Browse Available Risks
              </mui.Button>
            </mui.Paper>
          ) : filteredMappedRisks.length === 0 ? (
            <mui.Paper 
              sx={{ 
                p: 4, 
                textAlign: 'center',
                backgroundColor: '#f9f9f9',
                borderRadius: 2
              }}
            >
              <mui.Typography variant="h6" color="textSecondary" gutterBottom>
                No risks match your filter criteria
              </mui.Typography>
              <mui.Typography variant="body2" color="textSecondary">
                Try adjusting your search term or filter settings.
              </mui.Typography>
              <mui.Button 
                variant="outlined" 
                color="primary" 
                sx={{ mt: 2 }}
                onClick={() => {
                  setMappedSearchTerm('');
                  setMappedRiskTypeFilter('all');
                  // Clear filter values from localStorage
                  localStorage.removeItem(`risk-identifier-mapped-search-${appId}-${companyId}`);
                  localStorage.removeItem(`risk-identifier-mapped-type-filter-${appId}-${companyId}`);
                }}
              >
                Clear Filters
              </mui.Button>
            </mui.Paper>
          ) : viewMode === 'grid' ? (
            <mui.Grid container spacing={2}>
              {filteredMappedRisks.map(risk => (
                <mui.Grid item xs={12} sm={6} md={4} key={risk.id}>
                  <RiskCard 
                    risk={risk} 
                    isRelevant={true} 
                    onAction={handleRemoveRisk}
                  />
                </mui.Grid>
              ))}
            </mui.Grid>
          ) : viewMode === 'list' ? (
            <mui.Box>
              {filteredMappedRisks.map(risk => (
                <RiskListItem
                  key={risk.id}
                  risk={risk}
                  isRelevant={true}
                  onAction={handleRemoveRisk}
                />
              ))}
            </mui.Box>
          ) : (
            <mui.Box>
              {Object.entries(groupRisksByType(filteredMappedRisks)).map(([type, risks]) => 
                risks.length > 0 && (
                  <mui.Box key={type} sx={{ mb: 4 }}>
                    <mui.Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        mb: 2,
                        pb: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <mui.Avatar 
                        sx={{ 
                          bgcolor: 'primary.main', 
                          width: 32, 
                          height: 32,
                          mr: 1
                        }}
                      >
                        {getRiskTypeIcon(type)}
                      </mui.Avatar>
                      <mui.Typography variant="h6" color="primary.main">
                        {type} Risks
                      </mui.Typography>
                      <mui.Chip 
                        label={risks.length} 
                        size="small" 
                        sx={{ ml: 2 }} 
                        color="primary"
                        variant="outlined"
                      />
                    </mui.Box>
                    <mui.Grid container spacing={2}>
                      {risks.map(risk => (
                        <mui.Grid item xs={12} sm={6} md={4} key={risk.id}>
                          <RiskCard 
                            risk={risk} 
                            isRelevant={true} 
                            onAction={handleRemoveRisk}
                          />
                        </mui.Grid>
                      ))}
                    </mui.Grid>
                  </mui.Box>
                )
              )}
            </mui.Box>
          )}
        </TabPanel>
      </TabContext>
      
      {/* Bottom action bar */}
      <mui.Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          pt: 4,
          mt: 4,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}
      >
        <mui.Button
          variant="contained"
          color="primary"
          onClick={nextStep}
          disabled={mappedRisks.length === 0}
        >
          Continue to Risk Rating
        </mui.Button>
      </mui.Box>
    </mui.Box>
  );
};

export default RiskIdentifier; 